<!--
A study space in the Study Spaces Occupancy screen.
Usage: <StudySpace />

@author cm359n
-->
<template>
  <div class="ss-content">
      <table :class="'study-space-content' + (firstSpace ? ' first-space' : '')">
          <tr>
            <td class='ss-left'>
            <p v-if="fakeTime" style="color:red;">This Study Space thinks the time is {{ fakeTime }}</p>
            <table class="ss-info"><tr>
            <td><span class="ss-title">{{ name }}</span></td></tr>
            <tr>
            <td>
            <span class="ss-time">
                <!-- img alt="opening times" src="@/assets/schedule_24px.jpg" class="whiteclock" / -->
                <font-awesome-icon class="ss-clock" :icon="['far', 'clock']" size="sm"></font-awesome-icon>
                {{ timeDisplay() }}
                <span v-if="hasWeekSchedule">
                  <a @click="toggleWeekDisplay()">
                    <img v-if="!displayWeekSchedule" title="view all opening times"
                      alt="view all opening times" class="openingtimes-dropdown"
                      src="@/assets/br_down.svg" />
                    <img v-if="displayWeekSchedule" title="only show today's opening times"
                      alt="only show today's opening times" class="openingtimes-dropdown"
                      src="@/assets/br_up.svg" />
                  </a>
                </span>
            </span>
            <span>
            <OpeningTimes :class="'opentimes-' + ((displayWeekSchedule) ? 'open' : 'closed')" :schedule="sortedSchedule(schedule)" :currentDay="currentDay" />
            </span>
            </td></tr></table>
            </td>
            <td class="spacefor">
            <div v-if="hasOverride" role="img" class="space0" aria-label="unknown"> </div>
            <div v-if="!hasOverride" role="img" :class="roomInd().class" :aria-label="roomInd().desc"> </div>
            <div v-if="displayLevels" class="spacefor">
            <table><tr>
            <td v-if="!spaceIsClosed && !hasOverride" class="spacefor-display">Room for </td>
            <td v-if="!spaceIsClosed && !hasOverride" :class="'num-' + roomInd().class">{{ spacesAvailable }}</td>
            </tr></table>
            </div>
            <div v-if="!hasOverride && !displayLevels" class="spacespace">{{ roomInd().desc }}</div>
            <div v-if="!hasOverride && displayLevels && spaceIsClosed" class="spacespace">Closed</div>
            </td>
          </tr>
      </table>
      <table class="study-space-footer">
        <tr>
          <td><FacilityList :list="facilities" /></td>
        </tr>
        <tr>
          <td class="ss-fire-alarm">{{ fireAlarmTime != '' ? 'Fire alarm test ' : '' }}<strong>{{ fireAlarmTime }}</strong></td>
        </tr>
        <tr>
          <span v-if="lastUpdated" class="ss-updated">Last updated: {{ updateDisplay(lastUpdated) }}</span>
          <span v-if="!lastUpdated" class="ss-updated"> </span>
        </tr>
      </table>
  </div>
</template>

<script setup>
import {ref, onMounted} from 'vue'
//Components
import OpeningTimes from '@/components/spaces/OpeningTimes.vue'
import FacilityList from '@/components/spaces/FacilityList.vue'

//Composables
import {useStringUtil} from '@/composables/StringMethods.js'
const {isEmpty, chopSeconds, shortDay} = useStringUtil()

const props = defineProps({
  closeTime: String,
  currentlyUsed: Boolean,
  facilities: Array,
  fakeDayOfWeek: String,
  fakeTime: String,
  firstSpace: Boolean,
  lastUpdated: Number,
  name: String,
  occupancyStatus: Number,
  openTime: String,
  overrideMsg: String,
  schedule: Object,
  spacesAvailable: Number
})

//Data
const dayArray = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] // to match the schedule we get from Occupancy.vue
let d = new Date()
let dl = false
if (typeof props.spacesAvailable !== 'undefined') {
  dl = true
}
let dy = ref(dayArray[d.getDay()].toUpperCase())
if (typeof props.fakeDayOfWeek !== 'undefined') {
  dy = props.fakeDayOfWeek
}
const effOpenTime = ref()
const effCloseTime = ref()
const currentDay = ref(dy)
const hasWeekSchedule = ref(false)
const displayWeekSchedule = ref(false)
const displayLevels = ref(dl)
const spaceIsClosed = ref(false)
const hasOverride = ref(!isEmpty(props.overrideMsg))
const fireAlarmTime = ref()



onMounted(
  () => {
    checkSchedule()
    workoutSpaceClosed()
    determineFireAlarmTime()
  }
)


function sortedSchedule () {
  // we don't receive the schedule in order
  let s = props.schedule

  let ret = { 'Sun': s['SUNDAY'],
    'Mon': s['MONDAY'],
    'Tue': s['TUESDAY'],
    'Wed': s['WEDNESDAY'],
    'Thu': s['THURSDAY'],
    'Fri': s['FRIDAY'],
    'Sat': s['SATURDAY'] }
  return ret
}

function determineFireAlarmTime() {
  switch (props.name.toLowerCase()) {
    case 'james mccune smith learning hub':
      fireAlarmTime.value = 'Monday 06:00 - 07:00'
      break
    case 'main library':
      fireAlarmTime.value = 'Tuesday 06:00 - 07:00'
      break
    case 'mcmillan reading room':
      fireAlarmTime.value = 'Tuesday 07:30 - 08:30'
      break
    case 'fraser building level 3':
      fireAlarmTime.value = 'Friday 07:00 - 08:00'
      break
    default:
      fireAlarmTime.value = ''
  }
}

function toggleWeekDisplay () {
  displayWeekSchedule.value = !displayWeekSchedule.value
}

//Check if schedule exists and set the open and close times
function checkSchedule () {
  const dy = currentDay.value
  // console.log(this.name + ' schedule is ' + this.schedule)
  // do we have the times by day-of-week though?
  if (props.schedule != null && typeof props.schedule !== 'undefined') {
    hasWeekSchedule.value = true
    if (typeof props.schedule[dy] !== 'undefined') {
      const schedData = props.schedule[dy]
      effOpenTime.value = schedData.openTime
      effCloseTime.value = schedData.closeTime
      // console.log(this.name + "'s opening time is " + this.effOpenTime)
      // console.log(this.name + "'s closing time is " + this.effCloseTime)
    } else {
      console.log('schedule is present but does not contain data for ' + dy)
    }
  }
}

function newDate () {
  let d = new Date()
  if (typeof props.fakeTime !== 'undefined') {
    let hhmm = props.fakeTime.split(':')

    d.setHours(parseInt(hhmm[0]))
    d.setMinutes(parseInt(hhmm[1]))
  }
  return d
}

//Last updated message
function updateDisplay (d) {
  if (d == null) {
    return 'never updated'
  }
  let dte = newDate()
  let curr = newDate()
  dte.setTime(d)

  const msInDay = (1000 * 60 * 60 * 24)
  let daysAgo = Math.floor((curr.getTime() - dte.getTime()) / msInDay)
  let hoursAgo = curr.getHours() - dte.getHours()
  // console.log('getDate() = ' + curr.getDate() + ' - ' + dte.getDate())
  // console.log('getMonth() = ' + curr.getMonth() + ' - ' + dte.getMonth())
  // console.log('getYear() = ' + curr.getFullYear() + ' - ' + dte.getFullYear())

  // console.log(daysAgo + ' days ago')
  if (daysAgo > 7) {
    return 'more than a week ago'
  } else if (daysAgo >= 1 && daysAgo <= 7) {
    return daysAgo + ((daysAgo == 1) ? ' day ago' : ' days ago')
  } else if (hoursAgo > 1) {
    return hoursAgo + ' hours ago'
  } else {
    // console.log('space ' + this.name + ' updated at ' + dte)
    return 'less than an hour ago'
  }
}

//Opening hours for today
function timeDisplay () {
  if (!isEmpty(props.overrideMsg)) {
    return props.overrideMsg
  }
  let ret = 'CLOSED'
  if (isEmpty(effOpenTime.value) || isEmpty(effCloseTime.value)) {
    // console.log(this.name + ' is missing openTime or closeTime')
    // let dispMsg = 'CLOSED'
    // return dispMsg
  } else {
    let st = chopSeconds(effOpenTime.value)
    let et = chopSeconds(effCloseTime.value)
    // if we have a week schedule, '<today> <start> - <end>' else '<start> - <end>'
    ret = st + ' - ' + et
  }
  if (hasWeekSchedule.value) {
    ret = shortDay(currentDay.value) + ' ' + ret
  }
  return ret
}

function cd (i) {
  return (i + '').padStart(2, '0')
}

function roomInd () {
  let s = props.occupancyStatus // 1 = lots of, 2 = some, 3 = limited space
  const spacious = ['Closed', 'Spaces', 'Some', 'Limited']
  const sClosed = { class: 'spaceclosed', desc: spacious[0] }
  const sHide = { class: 'space0', desc: 'not shown' }
  const classType = 'space'

  // is the space closed?
  if (!isSpaceOpen()) {
    return sClosed
  }

  // are we hiding the indicator for some reason (e.g. override message being shown)?
  if (hasOverride.value) {
    return sHide
  }

  // the class determines the image we use (quiet/busy/full graphs)
  switch (s) {
    case 1:
    case 2:
    case 3:
      return { class: classType + s, desc: spacious[s] }
    default:
      return sClosed
  }
}

function isSpaceOpen () {
  return !spaceIsClosed.value
}

function workoutSpaceClosed () {
  let ct = effCloseTime.value
  let ot = effOpenTime.value
  let d = newDate()
  let hms = cd(d.getHours()) +
            ':' + cd(d.getMinutes()) +
            ':' + cd(d.getSeconds())

  if (ct < ot) {
    let checkOpen = (hms > ct && hms >= ot)
    let morningOpen = (hms < ct)
    let compareOpen = checkOpen || morningOpen
    spaceIsClosed.value = !(compareOpen)
  } else if (ct == ot && ct != null) {
    spaceIsClosed.value = false
  } else {
    let todayHasOpeningTimes = effOpenTime.value != null && effCloseTime.value != null
    let betweenOpeningTimes = (hms >= effOpenTime.value && hms <= ct)
    const isOpen = todayHasOpeningTimes && betweenOpeningTimes
    spaceIsClosed.value = !isOpen
  }
}

</script>

<style>
div.ss-content {
  width: 100%;
}
table.study-space-footer {
  width: 100%;
  margin-top: 10px;
  border-bottom: 1px solid #ddd;
}
table.study-space-content {
  width: 100%;
}
table.study-space-content.first-space {
    border-top: 1px solid #ddd;
}
td.ss-left {
    width: 95%;
    height:62px;
}
.ss-title {
    font-style: normal;
    font-weight: bold;
    font-size: 16px;
    line-height: 21px;
}
.ss-clock {
  color: #003865;
}
.ss-time {
    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 21px;
    color: #828282;
    white-space: nowrap;
}
.ss-fire-alarm {
  float: right;
  color: #003865;
  font-size: 13px;
}
td.spacefor {
  text-align: right;
  vertical-align: bottom;
}
td.spacefor-display {
  vertical-align: bottom;
  /* min-width: 110px; */
  color: #828282;
}

div.spacefor {
    font-family: Arial;
    font-style: normal;
    font-weight: normal;
    font-size: 13px;
    line-height: 15px;
    vertical-align: bottom;
}
div.space1 {
    width: 72px;
    height: 62px;
    background: url('~@/assets/rag-green.svg') no-repeat;
    background-position: center;
    float: right;
}
div.space2 {
    width: 72px;
    height: 62px;
    background: url('~@/assets/rag-amber.svg') no-repeat;
    background-position: center;
    float: right;
}
div.space3 {
    width: 72px;
    height: 62px;
    background: url('~@/assets/rag-red.svg') no-repeat;
    background-position: center;
    float: right;
}
div.space0 {
  width: 72px;
  height: 62px;
  float: right;
}

.num-space1 {
  font-style: normal;
  font-weight: bold;
  font-size: 20px;
  color: #006630;
  padding: 0px;
}
.num-space2 {
  font-style: normal;
  font-weight: bold;
  font-size: 20px;
  color: #FFC400;
  padding: 0px;
  vertical-align: bottom;
}
.num-space3 {
  font-style: normal;
  font-weight: bold;
  font-size: 20px;
  color: #FE416D;
  padding: 0px;
}
.spacespace {
    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 21px;
    text-align: center;
    color: #71717B;
}

.ss-updated {
  font-size: 12px;
  padding-left: 5px;
}

div.spaceclosed {
  width: 72px;
  height: 37px;
  background: url('~@/assets/redCross.svg') no-repeat;
  background-position: center;
}

.opentimes-closed {
  display: block;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s ease-out;
}
.opentimes-open {
  display: block;
  max-height: 333px;
  transition: max-height 0.4s ease-out;
}

</style>
